/**
 * Перевод движения руки в клетки сетки (тикет T18b).
 *
 * Вынесено из вёрстки нарочно: это единственная часть перетаскивания, которую можно
 * проверить без браузера, и именно в ней ошибаются — в округлении, в краях и в том, что
 * Блок нельзя вытащить за пределы сетки.
 *
 * Правила сетки (сколько клеток, что с чем не пересекается) живут в ядре клиента и здесь
 * не повторяются: тут только «на сколько клеток сдвинулась рука».
 */
import { GRID_COLUMNS } from '@core/layout/grid.js';
import type { Position } from '@core/layout/grid.js';

/** Размер одной клетки на экране, в пикселях. */
export interface CellSize {
  readonly width: number;
  readonly height: number;
}

/**
 * Сколько клеток прошла рука. Округляем к ближайшей: Блок должен вставать туда, куда его
 * тянут, а не отставать на полклетки, пока границу не перейдут целиком.
 */
export const cellsMoved = (
  pixels: { readonly x: number; readonly y: number },
  cell: CellSize,
): { readonly cols: number; readonly rows: number } => ({
  // Нулевая ширина клетки бывает у ещё не показанного экрана: делить на неё — получить
  // бесконечность и уронить перетаскивание в первый же кадр.
  cols: cell.width > 0 ? Math.round(pixels.x / cell.width) : 0,
  rows: cell.height > 0 ? Math.round(pixels.y / cell.height) : 0,
});

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

/**
 * Новое место Блока после сдвига. Держим внутри сетки: за левый край и выше первой строки
 * Блок не уходит, за правый — не вылезает. Иначе человек «терял» бы Блок, вытащив его за
 * край, а сервер потом всё равно отказал бы — уже после того, как мышь отпустили.
 */
export const movedTo = (
  position: Position,
  by: { readonly cols: number; readonly rows: number },
): Position => ({
  ...position,
  col: clamp(position.col + by.cols, 0, GRID_COLUMNS - position.width),
  row: Math.max(0, position.row + by.rows),
});

/**
 * Новый размер Блока после растягивания. Левый верхний угол остаётся на месте — тянут за
 * правый нижний. Меньше наименьшего размера Блок не сжимается, шире сетки не становится.
 */
export const resizedTo = (
  position: Position,
  by: { readonly cols: number; readonly rows: number },
  minimum: { readonly width: number; readonly height: number },
): Position => ({
  ...position,
  width: clamp(position.width + by.cols, Math.max(1, minimum.width), GRID_COLUMNS - position.col),
  height: Math.max(Math.max(1, minimum.height), position.height + by.rows),
});

/** Одинаковы ли места. Нужно, чтобы не слать на сервер правку, которой не было. */
export const samePosition = (a: Position, b: Position): boolean =>
  a.col === b.col && a.row === b.row && a.width === b.width && a.height === b.height;
